import { ReactNode, useId, useRef } from 'react';
import { useUncontrolled } from '@mantine/hooks';
import { KeyCode } from 'ag-grid-community';

import { FormLabel } from '../formlabel/FormLabel';
import { NumberInput } from '../numberinput/NumberInput';

import datePickerStyles from '../datepicker/date-picker.module.scss';
import styles from './number-range.module.scss';

export type NumberRange = {
  from?: number;
  to?: number;
};

export type NumberRangeProps = {
  allowDecimal?: boolean;
  clearable?: boolean;
  defaultValue?: NumberRange;
  disabled?: boolean;
  factor?: number;
  label?: ReactNode | ReactNode[];
  loading?: boolean;
  placeholderFrom?: string;
  placeholderTo?: string;
  suffix?: string;
  value?: NumberRange;
  onChange?: (range: NumberRange) => void;
  onCommit?: (range: NumberRange) => void;
};

const getNumericalValue = (
  value: string | number | undefined
): number | undefined => {
  if (typeof value === 'number' || value === undefined) {
    return value;
  }

  const numericalValue = Number(value);

  if (value.length === 0 || Number.isNaN(numericalValue)) {
    return undefined;
  }

  return numericalValue;
};

export function NumberRange({
  allowDecimal = false,
  clearable,
  defaultValue = {},
  disabled,
  factor,
  label,
  loading,
  placeholderFrom = 'From',
  placeholderTo = 'To',
  suffix,
  value,
  onChange,
  onCommit,
}: NumberRangeProps) {
  const id = useId();
  const isDisabled = loading || disabled;
  const fromRef = useRef<HTMLInputElement>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const [range, setRange] = useUncontrolled({
    defaultValue,
    value,
    onChange: (newRange) => {
      onChange?.(newRange);
    },
  });

  function commit(rangeToCommit: NumberRange) {
    setTimeout(() => {
      const focused = document.activeElement;
      if (fromRef.current !== focused && toRef.current !== focused) {
        onCommit?.(rangeToCommit);
      }
    });
  }

  return (
    <div data-testid="number-range">
      <FormLabel id={id} label={label} disabled={disabled} loading={loading} />
      <div className={styles['wrapper']}>
        <NumberInput
          ref={fromRef}
          allowDecimal={allowDecimal}
          clearable={clearable}
          disabled={disabled}
          factor={factor}
          id={id}
          loading={loading}
          onBlur={() => {
            const currentTo = getNumericalValue(range.to);
            const currentFrom = getNumericalValue(range.from);

            const newRange = {
              from:
                typeof currentFrom === 'number'
                  ? Math.min(currentFrom, currentTo ?? Number.MAX_SAFE_INTEGER)
                  : undefined,
              to: range.to,
            };

            if (newRange.from !== range.from || newRange.to !== range.to) {
              setRange(newRange);
            }

            commit(newRange);
          }}
          onChange={(value) =>
            setRange({
              from: getNumericalValue(value),
              to: range.to,
            })
          }
          onClear={() => {
            const newRange = { to: range.to, from: undefined };
            setRange(newRange);
            commit(newRange);
          }}
          onKeyUp={({ code }) => {
            if (KeyCode.ENTER === code) {
              // blur and thus trigger commit hook
              toRef.current?.focus();
            }
          }}
          placeholder={placeholderFrom}
          suffix={suffix}
          testId="range-from"
          value={range.from}
        />
        <div
          className={datePickerStyles['input-separator']}
          data-disabled={isDisabled}
        >
          -
        </div>
        <NumberInput
          ref={toRef}
          allowDecimal={allowDecimal}
          clearable={clearable}
          disabled={disabled}
          factor={factor}
          loading={loading}
          onBlur={() => {
            const currentTo = getNumericalValue(range.to);
            const currentFrom = getNumericalValue(range.from);

            const newRange = {
              from: range.from,
              to:
                typeof currentTo === 'number'
                  ? Math.max(currentTo, currentFrom ?? Number.MIN_SAFE_INTEGER)
                  : undefined,
            };

            if (newRange.from !== range.from || newRange.to !== range.to) {
              setRange(newRange);
            }

            commit(newRange);
          }}
          onChange={(value) => {
            setRange({
              from: range.from,
              to: getNumericalValue(value),
            });
          }}
          onClear={() => {
            const newRange = {
              from: range.from,
              to: undefined,
            };
            setRange(newRange);
            commit(newRange);
          }}
          onKeyUp={({ code }) => {
            if (KeyCode.ENTER === code) {
              // blur and thus trigger commit hook
              toRef.current?.blur();
            }
          }}
          placeholder={placeholderTo}
          suffix={suffix}
          testId="range-to"
          value={range.to}
        />
      </div>
    </div>
  );
}
